export default `
  **Last updated: August 28, 2025**
  
  Nurau Inc. (“Nurau,” “we,” “our,” or “us”) is committed to protecting the privacy and personal information of individuals using the Nurau mobile application (the “App”). This Privacy Policy explains how we collect, use, disclose, retain, and protect personal information in compliance with:
  - Québec's Act to modernize legislative provisions respecting the protection of personal information (Law 25),
  - Canada's Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial laws, and
  - the European Union General Data Protection Regulation (GDPR), where applicable.
  
  **By using the App, you acknowledge and consent to the practices described in this Privacy Policy, subject to your rights under applicable laws.**
  
#### 1. Accountability and Governance  
  
  **Privacy Officer**  
  Nurau Inc. has designated a Privacy Officer responsible for compliance with privacy laws, including Law 25.
  
  Contact Information  
  Me Ava Chisling, B.A., B.C.L., LL.B.  
  Lawyer  
  Nurau Inc.  
  3978 St-André, Montréal, Québec H2L 3W1  
  Email: legal@nurau.com  
  
  We will respond to all privacy inquiries promptly, in accordance with applicable laws.
  
#### 2. Scope
  This Privacy Policy applies to personal information collected, used, disclosed, and retained through the Nurau App. It does not apply to external websites or third-party applications.
  
#### 3. Information We Collect
  We collect only the personal information necessary to operate and improve the App, in accordance with the principle of data minimization.
  
  **a. Categories of information collected:**
  - Account Information: Name, email address, organization name, and login credentials.
  - Company Policy Inputs: Content uploaded or accessed by managers, limited to organizational policies (not personal employee records).
  - Usage Data: Device type, operating system, app version, IP address, and usage activity.
  - Support Information: Data provided when contacting us for support.
  - Sensitive Information: The App is not designed to collect sensitive personal data (e.g., health, biometrics, social insurance numbers).
  
  **b. Collection Notice:**
  At the time of collection, you will be informed of:
  1. The purposes for which the information is collected.
  2. The means by which it is collected.
  3. Your rights of access, rectification, and withdrawal of consent.
  4. The possibility that information may be disclosed outside Québec, if applicable.
  
#### 4. Purposes of Collection
  We use personal information strictly for the following purposes:
  - To provide and operate the App.
  - To generate AI-assisted responses to workplace questions, based on company policies.
  - To improve functionality, security, and user experience.
  - To comply with legal obligations.
  - To communicate essential updates.
  
  **Nurau acts as a passive tool: the App does not replace managerial or union decision-making. Human review is always required.**
  
#### 5. Consent
  - Consent is explicit, free, informed, and specific
  - Consent is collected separately from other terms (e.g., Terms of Use).
  - Users may withdraw consent at any time, subject to legal or contractual restrictions.
  - This app is not intended for minors and should not be used by minors.
  
#### 6. Legal Basis for Processing (GDPR)
  Where GDPR applies, processing is based on:
  - Consent (for optional features).
  - Contractual necessity (to deliver services).
  - Legitimate interest (improving security and functionality).
  - Legal obligations (compliance with Québec and Canadian laws).
  
#### 7. Use and Disclosure of Information
  Personal information may only be disclosed:
  - With your express consent.
  - To service providers under contractual obligations to comply with Law 25 and PIPEDA.
  - As required by law, subpoena, or investigation.
  - In the context of a business transaction (e.g., merger, sale of assets), with appropriate safeguards.
  - In aggregate or anonymized form for statistical purposes.
  
  We do not sell or rent personal information.
  
  **Employee Data Disclaimer**  
  The App is designed to process organizational policies and general workplace guidance. It is not intended to process individual employee files. Any disclosure of employee personal information must be lawful and authorized by the employer.
  
#### 8. Cookies and Tracking Technologies
  The App may use cookies or equivalent technologies such as mobile SDKs:
  - Essential: Required for security, authentication, or core functionality.
  - Analytics/Performance: To understand app usage and improve services.
  - Non-Essential Tracking: Disabled by default and requires your express opt-in consent.
  
  You can manage or withdraw consent through your device settings.
  
#### 9. Cross-Border Transfers
  Where personal information is stored or processed outside Québec:
  - Nurau will conduct and document a Privacy Impact Assessment (PIA)
  - Transfers will occur only if the jurisdiction provides adequate protection, or with contractual safeguards.
  
  
#### 10. Retention and Destruction
  Personal information will be retained only as long as necessary for the purposes identified, or as required by law.
  
  **Retention schedule:**
  - Account data: 2 months after account closure.
  - Usage logs: up to 12 months
  - Support data: up to 24 months
  - Backups: 90 days
  
  After the retention period, data will be securely destroyed or anonymized.
  
#### 11. Automated Decision-Making and AI Use
  - The App uses AI to generate suggested responses.
  - AI is passive and does not independently make decisions affecting employees.
  - All outputs require human validation.
  - No profiling or automated decision-making with legal or significant effects is carried out without your express consent.
  
  
#### 12. User Rights
  You have the following rights under Law 25, PIPEDA, and GDPR:
  - Access: Obtain a copy of your personal information.
  - Rectification: Correct inaccurate or incomplete information.
  - Deletion: Request erasure of your personal information.
  - Portability: Receive your data in a structured, machine-readable format.
  - Restriction/Objection: Limit or object to processing in certain cases.
  - De-indexation/Re-indexation: Request removal or correction of public-facing information.
  - Withdraw consent: At any time, subject to legal obligations.
  
  Requests should be sent to legal@nurau.com. We will verify your identity and respond within 30 days.
  
  
#### 13. Security Measures
  We implement technical and organizational safeguards to protect personal information, including:
  - Encryption in transit and at rest.
  - Access restrictions and role-based controls.
  - Logging and monitoring of system activity.
  - Secure destruction procedures.
  
#### 14. Confidentiality Incidents and Breach Notification
  If a confidentiality incident occurs (unauthorized access, use, or disclosure):
  - We will assess the risk of serious harm.
  - If applicable, we will notify affected individuals and the Commission d'accès à l'information du Québec (CAI) without delay.
  - We maintain an internal log of all confidentiality incidents, available to the CAI upon request.
  
#### 15. Privacy by Default and Design
  The App is designed so that:
  - Only personal information strictly necessary for each purpose is collected.
  - Confidentiality settings are set to the highest level by default.
  - Privacy protection is embedded in design choices
  
#### 16. Children's Privacy
  The App is not directed at children and should not be used by children.
  
#### 17. Changes to the Policy
  We may update this Privacy Policy from time to time. Any material changes will be communicated through in-app notices or email. Continued use of the App after updates indicates acceptance.
  
#### 18. Governing Law and Jurisdiction
  This Privacy Policy and any disputes related to it are governed by the laws of Québec and the laws of Canada applicable therein. Any disputes shall be submitted to the exclusive jurisdiction of the courts of Montréal, Québec.
  
  
#### 19. Contact
  Nurau Inc.  
  3978 St-André  
  Montréal, Québec H2L 3W1  
  Canada  
  Email: legal@nurau.com  
`;
